import React, { useEffect, useMemo, useRef } from 'react'
import * as yup from 'yup'

import {
  Button,
  Form,
  FormButtons,
  OptionSelector,
  TextInput,
} from '@/components'
import CarSelector from '@/pages/Car/carSelector'
import { getMaskedString, print } from '@/utils'
import { ComplaintsReportSourceChoices } from '@/apollo/graphql'
import { IReport } from '@/types'
import { useForm } from '@/hooks'
import { t } from '@/i18n'

import { useOptions } from '../useOptions'

interface Props {
  loading: boolean
  isEditing?: boolean
  submitting: boolean
  onCancel: () => void
  initialData?: Partial<IReport>
  onSubmit: (d: IReport) => void
}

const ReportForm: React.FC<Props> = ({
  loading,
  onCancel,
  onSubmit,
  isEditing,
  submitting,
  initialData,
}) => {
  const { reportSourceOptions, locationOptions } = useOptions()
  const schema = useMemo(
    () =>
      yup
        .object({
          reportNumber: yup.string().required(t('required_field_error')),
          issueTime: yup.string().required(t('required_field_error')),
          crimeTime: yup.string().required(t('required_field_error')),
          source: yup
            .mixed<ComplaintsReportSourceChoices>()
            .oneOf(
              Object.values(ComplaintsReportSourceChoices),
              t('required_field_error'),
            ),
        })
        .required(),
    [],
  )

  const { control, isValid, getValues, onTriggerChange, onFormSubmit } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  const formRef = useRef<HTMLFormElement | null>(null)

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  useEffect(() => {
    if (initialData?.car) {
      onTriggerChange('car', initialData.car.id)
    }
  }, [initialData?.car, onTriggerChange])

  useEffect(() => {
    if (initialData?.carNumber) {
      onTriggerChange('carNumber', getMaskedString(initialData.carNumber, true))
    }
  }, [onTriggerChange, initialData?.carNumber])

  return (
    <>
      <div className="main-info">
        <div className="close-box">
          <Button onClick={onCancel}>
            <span className="icon-close"></span>
          </Button>
        </div>
        <div className="section-box">
          <div className="toolbox">
            <div className="title-box">
              <h4>{t('edit_report')}</h4>
            </div>
            <div className="btn">
              <Button
                className="main-btn light"
                onClick={() => print('report', initialData?.id)}
              >
                <span className="icon-print"></span>
                {t('print')}
              </Button>
            </div>
          </div>

          <div className="edit-data">
            <Form<IReport>
              formRef={formRef}
              control={control}
              isEditing={isEditing}
              onSubmit={onFormSubmit}
            >
              <div className="form-fields">
                <TextInput
                  required
                  className="w-50"
                  control={control}
                  name={'reportNumber'}
                  emptyValue={emptyValue}
                  label={t('report_number')}
                  disabled={!Boolean(isEditing)}
                />
                <CarSelector
                  isFormElement
                  className="w-50"
                  control={control}
                  menuPlacement="bottom"
                  isEditing={isEditing}
                  initial={initialData?.car}
                  selected={getValues('car')}
                  fallbackEl={(props: any) =>
                    initialData?.carNumber ? (
                      <TextInput
                        {...props}
                        disabled
                        isCarNumber
                        control={control}
                        name="carNumber"
                        emptyValue={emptyValue}
                        label={t('car_number')}
                      />
                    ) : null
                  }
                />
                <TextInput
                  className="w-50"
                  control={control}
                  name={'issueTime'}
                  label={t('issue_time')}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                />
                <TextInput
                  className="w-50"
                  control={control}
                  name={'crimeTime'}
                  label={t('crime_time')}
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                />
                <OptionSelector
                  required
                  name="source"
                  className="w-50"
                  control={control}
                  label={t('source')}
                  menuPlacement="bottom"
                  emptyValue={emptyValue}
                  options={reportSourceOptions}
                  disabled={!Boolean(isEditing)}
                />
                <OptionSelector
                  name="location"
                  className="w-50"
                  control={control}
                  label={t('location')}
                  menuPlacement="bottom"
                  emptyValue={emptyValue}
                  options={locationOptions}
                  disabled={!Boolean(isEditing)}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
      <FormButtons
        formRef={formRef}
        visible={isEditing}
        onCancel={onCancel}
        isFormValid={isValid}
        submitting={submitting}
        cancelClassName={'gray'}
      />
    </>
  )
}

export default ReportForm
